import { useLocation, useParams } from "react-router-dom";

export interface DetailPageNavContext {
  /** Ordered sibling ids surfaced by the list page, or `[]` if none. */
  ids: readonly string[];
  /** The current route's id segment. */
  currentId: string;
  /** True only when there is a usable multi-record list to navigate. */
  hasContext: boolean;
}

function isStateWithStringIds(
  state: unknown,
): state is { ids: readonly string[] } {
  if (typeof state !== "object" || state === null) return false;
  const ids = (state as { ids?: unknown }).ids;
  if (!Array.isArray(ids)) return false;
  return ids.every((v) => typeof v === "string");
}

/**
 * Surfaces the ordered list of sibling record ids that a list page
 * attached to `navigate(…, { state: { ids } })` when the user opened
 * this detail page.
 *
 * List-page contract:
 *
 *   navigate(`/locations/${id}`, { state: { ids: rows.map(r => r.id) } });
 *
 * Refresh-safety: `useLocation().state` does not survive a hard reload.
 * A refresh therefore collapses to `hasContext: false` and the consuming
 * `<DetailPageNav>` hides. To make a particular entity's nav refresh-safe,
 * migrate that entity's list page from `state` to URL search params in a
 * follow-up — the hook contract here is unchanged.
 */
export function useDetailPageNavContext(): DetailPageNavContext {
  const { state } = useLocation();
  const { id } = useParams<{ id: string }>();
  const currentId = id ?? "";
  const ids =
    isStateWithStringIds(state) && state.ids.includes(currentId)
      ? state.ids
      : [];
  return { ids, currentId, hasContext: ids.length > 1 };
}
